/**
 * PdpMockStatusPage — admin-side batch-wise view of PDP / Mock Interview /
 * Video-Transcript trainer data-entry, at /training/pdp-mock-status.
 *
 * Purpose: give training managers a single place to spot which batches still
 * have missing PDP notes, missing mock statuses, or missing video/transcript
 * URLs — then export the sheet as Excel or PDF for the weekly review.
 *
 * Data source: GET /api/training/reports/pdp-mock-status
 */
import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { Loader2, FileSpreadsheet, FileText, ChevronDown, ChevronUp, Search, X, ExternalLink, ClipboardList, TrendingUp, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import api from '../../lib/api';

const STATUSES = ['Running', 'Upcoming', 'Completed', 'On Hold', 'all'];
const MISSING_FILTERS = [
  { key: 'all', label: 'All batches' },
  { key: 'missing_pdp', label: 'Missing PDP' },
  { key: 'missing_mock', label: 'Missing Mock' },
  { key: 'missing_link', label: 'Missing Video/Transcript' },
  { key: 'any_missing', label: 'Any Missing (< 100%)' },
];

// ─── Percent chip ────────────────────────────────────────────
function PctChip({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-slate-300">—</span>;
  const tone = pct >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : pct >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${tone} tabular-nums`}>{pct}%</span>;
}

// ─── Export helpers ──────────────────────────────────────────
function buildFlatRowsForExport(batches) {
  const rows = [];
  for (const b of batches) {
    rows.push({
      'Batch #': b.batch_number,
      'Batch Name': b.batch_name,
      'Trainer': b.trainer_name,
      'Course': b.course_name,
      'Type': b.batch_type,
      'Status': b.batch_status,
      'Start': b.start_date || '',
      'End': b.end_date || '',
      'Students': b.total_students,
      'PDP Filled': b.pdp_filled,
      'PDP %': b.pdp_pct ?? '',
      'Mock Filled': b.mock_filled,
      'Mock %': b.mock_pct ?? '',
      'Link Filled': b.link_filled,
      'Link %': b.link_pct ?? '',
      'Mock Completed': b.mock_completed,
      'Mock Scheduled': b.mock_scheduled,
      'Mock Not Done': b.mock_not_done,
    });
  }
  return rows;
}

function exportBatchesExcel(batches, filename) {
  const summary = buildFlatRowsForExport(batches);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Summary');
  // Second sheet: every student row across every batch
  const detail = [];
  for (const b of batches) {
    for (const s of (b.students || [])) {
      detail.push({
        'Batch #': b.batch_number,
        'Batch Name': b.batch_name,
        'Trainer': b.trainer_name,
        'Enrollment': s.enrollment_no,
        'Student': s.name,
        'PDP': s.pdp || '',
        'Mock Interview': s.mock_interview || '',
        'Video / Transcript Link': s.mock_interview_link || '',
        'Last Updated': s.updated_at || '',
        'Updated By': s.updated_by || '',
      });
    }
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), 'Students');
  const blob = new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' });
  saveAs(blob, `${filename}.xlsx`);
}

function exportBatchesPdf(batches, totals, title, filename) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt' });
  doc.setFontSize(14).setTextColor(50, 50, 90);
  doc.text(title, 40, 40);
  doc.setFontSize(9).setTextColor(120);
  doc.text(`${totals?.batches || 0} batches · ${totals?.students || 0} students · Overall PDP ${totals?.pdp_pct || 0}% · Mock ${totals?.mock_pct || 0}% · Link ${totals?.link_pct || 0}%`, 40, 58);
  const head = [['Batch #', 'Batch Name', 'Trainer', 'Status', 'Students', 'PDP %', 'Mock %', 'Link %', 'Completed', 'Scheduled', 'Not Done']];
  const body = batches.map(b => [
    b.batch_number, b.batch_name, b.trainer_name, b.batch_status, b.total_students,
    b.pdp_pct ?? '—', b.mock_pct ?? '—', b.link_pct ?? '—',
    b.mock_completed, b.mock_scheduled, b.mock_not_done,
  ]);
  autoTable(doc, {
    startY: 74, head, body,
    styles: { fontSize: 7, cellPadding: 3 },
    headStyles: { fillColor: [92, 55, 179], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 246, 253] },
    columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right' } },
  });
  doc.save(`${filename}.pdf`);
}

// ─── Main page ───────────────────────────────────────────────
export default function PdpMockStatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('Running');
  const [missingFilter, setMissingFilter] = useState('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/training/reports/pdp-mock-status', { params: { status: statusFilter } })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) toast.error('Failed to load PDP / Mock status'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [statusFilter]);

  const batches = useMemo(() => data?.batches || [], [data]);
  const totals = data?.totals || {};

  const filtered = useMemo(() => {
    let rows = batches;
    if (missingFilter !== 'all') {
      rows = rows.filter(b => {
        if (missingFilter === 'missing_pdp') return (b.pdp_pct || 0) < 100;
        if (missingFilter === 'missing_mock') return (b.mock_pct || 0) < 100;
        if (missingFilter === 'missing_link') return (b.link_pct || 0) < 100;
        if (missingFilter === 'any_missing') return (b.pdp_pct || 0) < 100 || (b.mock_pct || 0) < 100 || (b.link_pct || 0) < 100;
        return true;
      });
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      rows = rows.filter(b =>
        (b.batch_name || '').toLowerCase().includes(needle) ||
        (b.trainer_name || '').toLowerCase().includes(needle) ||
        String(b.batch_number || '').includes(needle) ||
        (b.course_name || '').toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [batches, missingFilter, q]);

  const doExcel = () => {
    if (!filtered.length) return toast.info('Nothing to export');
    exportBatchesExcel(filtered, `PDP_Mock_Status_${statusFilter}_${new Date().toISOString().slice(0, 10)}`);
    toast.success('Excel exported');
  };
  const doPdf = () => {
    if (!filtered.length) return toast.info('Nothing to export');
    exportBatchesPdf(filtered, totals, `PDP / Mock / Video Status — ${statusFilter}`, `PDP_Mock_Status_${statusFilter}_${new Date().toISOString().slice(0, 10)}`);
    toast.success('PDF exported');
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-violet-600" />
            PDP · Mock Interview · Video Status
          </h1>
          <p className="text-xs text-slate-500 mt-1">Batch-wise view of trainer data-entry. Spot missing PDP notes, missing mock statuses and missing video links before the weekly review.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={doExcel} data-testid="pdpmock-export-excel">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={doPdf} data-testid="pdpmock-export-pdf">
            <FileText className="w-3.5 h-3.5 mr-1" />PDF
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Batches" value={totals.batches ?? 0} icon={ClipboardList} tone="violet" testId="pdpmock-kpi-batches" />
        <Kpi label="Students" value={totals.students ?? 0} icon={TrendingUp} tone="slate" testId="pdpmock-kpi-students" />
        <Kpi label="PDP Fill %" value={`${totals.pdp_pct ?? 0}%`} icon={CheckCircle2} tone="emerald" testId="pdpmock-kpi-pdp" />
        <Kpi label="Mock Fill %" value={`${totals.mock_pct ?? 0}%`} icon={CheckCircle2} tone="amber" testId="pdpmock-kpi-mock" />
        <Kpi label="Link Fill %" value={`${totals.link_pct ?? 0}%`} icon={AlertTriangle} tone="rose" testId="pdpmock-kpi-link" />
      </div>

      {/* Filters */}
      <Card className="p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mr-1">Status</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="pdpmock-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {MISSING_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setMissingFilter(f.key)}
              className={`text-[11px] px-2.5 py-1 rounded border transition ${missingFilter === f.key ? 'bg-violet-600 border-violet-700 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid={`pdpmock-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search batch / trainer / course…" className="h-8 text-xs pl-7 w-64" data-testid="pdpmock-search" />
          {q && <button onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-3 h-3 text-slate-400" /></button>}
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-xs text-slate-400">No batches match your filters.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-[9px] uppercase tracking-wider">Batch</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider">Trainer</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider">Course</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-center">Students</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-center">PDP</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-center">Mock</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-center">Link</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-center">Mock: C / S / N</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(b => {
                const isOpen = !!expanded[b.batch_id];
                return (
                  <>
                    <TableRow key={b.batch_id}
                      className="cursor-pointer hover:bg-violet-50/40"
                      onClick={() => setExpanded(p => ({ ...p, [b.batch_id]: !p[b.batch_id] }))}
                      data-testid={`pdpmock-batch-${b.batch_number}`}>
                      <TableCell className="text-xs">
                        <p className="font-medium text-violet-700">#{b.batch_number}</p>
                        <p className="text-[10px] text-slate-500">{b.batch_name}</p>
                      </TableCell>
                      <TableCell className="text-xs text-slate-700">{b.trainer_name}</TableCell>
                      <TableCell className="text-[10px] text-slate-500">{b.course_name}</TableCell>
                      <TableCell className="text-xs text-slate-700 text-center tabular-nums">{b.total_students}</TableCell>
                      <TableCell className="text-center"><PctChip pct={b.pdp_pct} /></TableCell>
                      <TableCell className="text-center"><PctChip pct={b.mock_pct} /></TableCell>
                      <TableCell className="text-center"><PctChip pct={b.link_pct} /></TableCell>
                      <TableCell className="text-[10px] text-center tabular-nums">
                        <span className="text-emerald-700">{b.mock_completed}</span>
                        <span className="text-slate-300 mx-1">/</span>
                        <span className="text-amber-700">{b.mock_scheduled}</span>
                        <span className="text-slate-300 mx-1">/</span>
                        <span className="text-rose-700">{b.mock_not_done}</span>
                      </TableCell>
                      <TableCell className="text-center">{isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow key={`${b.batch_id}-drill`} className="bg-violet-50/30 hover:bg-violet-50/30">
                        <TableCell colSpan={9} className="p-0">
                          <BatchDrilldown batch={b} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = 'violet', testId }) {
  const tones = {
    violet: 'bg-violet-50 text-violet-700 border-violet-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
  };
  return (
    <Card className="p-3 border" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${tones[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</p>
          <p className="text-lg font-bold text-slate-900 tabular-nums leading-none mt-0.5">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function BatchDrilldown({ batch }) {
  const isSafeLink = (v) => { try { const u = new URL(v); return u.protocol === 'https:' || u.protocol === 'http:'; } catch { return false; } };
  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Students · {batch.total_students}</p>
        <p className="text-[10px] text-slate-500">Mock breakdown — <span className="text-emerald-700">Completed {batch.mock_completed}</span> · <span className="text-amber-700">Scheduled {batch.mock_scheduled}</span> · <span className="text-rose-700">Not Done {batch.mock_not_done}</span></p>
      </div>
      <div className="border border-slate-200 rounded overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[9px] uppercase tracking-wider">Enrollment</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider">Student</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider">PDP</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider">Mock</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider">Video / Transcript</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider">Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(batch.students || []).map(s => {
              const link = s.mock_interview_link;
              const mockTone = s.mock_interview === 'Completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : s.mock_interview === 'Scheduled' ? 'bg-amber-50 text-amber-700 border-amber-200'
                : s.mock_interview === 'Not Done' ? 'bg-rose-50 text-rose-700 border-rose-200'
                : 'bg-slate-50 text-slate-500 border-slate-200';
              return (
                <TableRow key={s.student_id}>
                  <TableCell className="text-[11px] font-medium text-violet-600">{s.enrollment_no}</TableCell>
                  <TableCell className="text-[11px] text-slate-700">{s.name}</TableCell>
                  <TableCell className="text-[11px] text-slate-600 max-w-[200px] truncate" title={s.pdp}>{s.pdp || <span className="text-slate-300 italic">missing</span>}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${mockTone}`}>{s.mock_interview || '— missing'}</Badge></TableCell>
                  <TableCell className="text-[10px] max-w-[200px] truncate">
                    {link
                      ? (isSafeLink(link) ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-0.5">{link.slice(0, 40)}{link.length > 40 ? '…' : ''}<ExternalLink className="w-2.5 h-2.5" /></a> : <span className="text-slate-500">{link}</span>)
                      : <span className="text-slate-300 italic">missing</span>}
                  </TableCell>
                  <TableCell className="text-[10px] text-slate-400">{s.updated_at ? String(s.updated_at).slice(0, 10) : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
